import { useState, useEffect } from 'react'
import { t } from '@lingui/core/macro'
import { useQueryClient, QueryObserver } from '@tanstack/react-query'
import { getPendingRows } from '@global/api'
import { showSnackbar } from '@global/snackbar/snackbar.actions'
import { useAppDispatch } from '@redux/hooks'
import { useDatasetPendingRows, KEY } from './useDatasetPendingRows'

export const useDatasetPendingRowsQuery = ({ datasetId }) => {
  const dispatch = useAppDispatch()
  const pendingRows = useDatasetPendingRows({ datasetId })
  const { isError, isLoading, error } = pendingRows

  useEffect(() => {
    if (!isError) return

    const errorMessage = (error as string) || t`Unknown error`
    dispatch(
      showSnackbar({
        type: 'ERROR',
        duration: 5000,
        message: t`Failed to load pending rows: ${errorMessage}`,
      }),
    )
  }, [isError])

  return {
    ...pendingRows,
    isLoading,
  }
}

/**
 * Observer hook for pending rows.
 * This hook will update the data in the query cache when the data changes.
 * @returns
 */
export const useDatasetPendingRowsObserver = ({ datasetId }) => {
  const queryClient = useQueryClient()

  const [pendingRows, setPendingRows] = useState(() => {
    const data = queryClient.getQueryData<string[]>([KEY, datasetId])
    return data ?? []
  })

  useEffect(() => {
    const observer = new QueryObserver<string[]>(queryClient, {
      queryKey: [KEY, datasetId],
      queryFn: ({ signal }) => getPendingRows({ datasetId, signal }),
    })

    const unsubscribe = observer.subscribe(result => {
      if (result.data) setPendingRows(result.data)
    })

    return () => {
      unsubscribe()
    }
  }, [])

  const addPendingRows = (newRows: string[]) => {
    // Get the current data from the query client
    const currentData = queryClient.getQueryData<string[]>([KEY, datasetId]) ?? []

    // Merge current data with new rows
    const updatedData = [...currentData, ...newRows]

    // Set the merged data back into the query client
    queryClient.setQueryData([KEY, datasetId], updatedData)

    // Update the local state as well
    setPendingRows(updatedData)
  }

  return {
    pendingRows,
    addPendingRows,
  }
}
